import { useQuery } from '@tanstack/react-query';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { CancellationResponse } from '../types/cancellations';
import {
	AutomatedUnderwritingResult,
	CancellationCreateRequest,
	FeeUpdateRequest,
	GraceLapseReinstatementResponse,
	PaymentScheduleChangeRequest,
	Policy2,
	PolicyFeeSummaryResponse,
	PolicyPriceResponse,
	PolicyQuoteResponse,
	PolicyQuoteUpdateRequest,
	PolicyUpdateRequest,
} from '../types/policy';
import { getApiUrl } from '../util/util';
import { get, patch, post } from './http';

interface PolicyAPI {
	createPolicy(
		authorizationToken: string,
		data: any,
	): Promise<AxiosResponse<Policy2>>;
	getPolicy(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<Policy2>>;
	getQuotes(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<PolicyQuoteResponse[]>>;
	updateQuote(
		authorizationToken: string,
		quoteLocator: string,
		data: PolicyQuoteUpdateRequest,
	): Promise<AxiosResponse<Policy2>>;
	createNewQuote(
		authorizationToken: string,
		quoteLocator: string,
	): Promise<AxiosResponse<Policy2>>;
	selectQuote(
		authorizationToken: string,
		quoteLocator: string,
	): Promise<AxiosResponse<Policy2>>;
	discardQuote(
		authorizationToken: string,
		quoteLocator: string,
	): Promise<AxiosResponse<Policy2>>;
	discardPolicy(
		authorizationToken: string,
		policyLocator: string,
	): Promise<AxiosResponse<{}>>;
	cancelPolicy(
		authorizationToken: string,
		policyLocator: string,
		params: CancellationCreateRequest,
	): Promise<AxiosResponse<CancellationResponse>>;
	priceQuote(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<PolicyPriceResponse>>;
	fetchQuotePricing(
		authorizationToken: string,
		quoteLocator: string,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<PolicyPriceResponse>>;
	getPolicyPricing(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<PolicyPriceResponse>>;
	acceptQuote(
		authorizationToken: string,
		quoteLocator: string,
	): Promise<AxiosResponse<Policy2>>;
	quoteQuote(
		authorizationToken: string,
		quoteLocator: string,
	): Promise<AxiosResponse<Policy2>>;
	issuePolicy(
		authorizationToken: string,
		policyLocator: string,
	): Promise<AxiosResponse<Policy2>>;
	invalidateQuote(
		authorizationToken: string,
		quoteLocator: string,
	): Promise<AxiosResponse<Policy2>>;
	getAutomatedUnderwritingResult(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<AutomatedUnderwritingResult>>;
	getGraceLapseAndReinstatementInfo(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<GraceLapseReinstatementResponse[]>>;
	getCancellationValue(
		authorizationToken: string,
		policyLocator: string,
		coverageEndTimestamp: string,
	): Promise<AxiosResponse<Policy2>>;
	withdrawPolicy(
		authorizationToken: string,
		policyLocator: string,
	): Promise<AxiosResponse<Policy2>>;
	updatePolicy(
		authorizationToken: string,
		policyLocator: string,
		params: PolicyUpdateRequest,
	): Promise<AxiosResponse<Policy2>>;
	finalizePolicy(
		authorizationToken: string,
		policyLocator: string,
	): Promise<AxiosResponse<Policy2>>;
	changePaymentPlan(
		authorizationToken: string,
		policyLocator: string,
		params: PaymentScheduleChangeRequest,
	): Promise<AxiosResponse<void>>;
	addFee(
		authorizationToken: string,
		policyLocator: string,
		params: FeeUpdateRequest,
	): Promise<AxiosResponse<PolicyFeeSummaryResponse>>;
}

const PolicyApi = {
	createPolicy(authorizationToken: string, data: any) {
		return post(`${getApiUrl()}/policy`, authorizationToken, data);
	},
	getPolicy(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	) {
		return get(
			`${getApiUrl()}/policy/${policyLocator}`,
			authorizationToken,
			config,
		);
	},
	getQuotes(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	) {
		return get(
			`${getApiUrl()}/policies/${policyLocator}/quotes`,
			authorizationToken,
			config,
		);
	},
	updateQuote(
		authorizationToken: string,
		quoteLocator: string,
		quoteUpdateObj: PolicyQuoteUpdateRequest,
	) {
		return patch(
			`${getApiUrl()}/quotes/${quoteLocator}`,
			authorizationToken,
			quoteUpdateObj,
		);
	},
	createNewQuote(authorizationToken: string, quoteLocator: string) {
		return post(
			`${getApiUrl()}/quotes/${quoteLocator}/createNew`,
			authorizationToken,
		);
	},
	selectQuote(authorizationToken: string, quoteLocator: string) {
		return patch(
			`${getApiUrl()}/quotes/${quoteLocator}/select`,
			authorizationToken,
		);
	},
	discardQuote(authorizationToken: string, quoteLocator: string) {
		return patch(
			`${getApiUrl()}/quotes/${quoteLocator}/discard`,
			authorizationToken,
		);
	},
	discardPolicy(authorizationToken: string, policyLocator: string) {
		return post(
			`${getApiUrl()}/policy/${policyLocator}/discard`,
			authorizationToken,
		);
	},
	cancelPolicy(
		authorizationToken: string,
		policyLocator: string,
		params: CancellationCreateRequest,
	) {
		return post(
			`${getApiUrl()}/policies/${policyLocator}/cancellations`,
			authorizationToken,
			params,
		);
	},
	priceQuote(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	) {
		return post(
			`${getApiUrl()}/policy/${policyLocator}/price`,
			authorizationToken,
			null,
			config,
		);
	},
	fetchQuotePricing(
		authorizationToken: string,
		quoteLocator: string,
		config?: AxiosRequestConfig<any>,
	) {
		return get(
			`${getApiUrl()}/quotes/${quoteLocator}/price`,
			authorizationToken,
			null,
		);
	},
	getPolicyPricing(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	) {
		return get(
			`${getApiUrl()}/policy/${policyLocator}/price`,
			authorizationToken,
			config,
		);
	},
	acceptQuote(authorizationToken: string, quoteLocator: string) {
		return patch(
			`${getApiUrl()}/quotes/${quoteLocator}/accept`,
			authorizationToken,
		);
	},
	quoteQuote(authorizationToken: string, quoteLocator: string) {
		return patch(
			`${getApiUrl()}/quotes/${quoteLocator}/quote`,
			authorizationToken,
		);
	},
	issuePolicy(authorizationToken: string, policyLocator: string) {
		return post(
			`${getApiUrl()}/policy/${policyLocator}/issue`,
			authorizationToken,
		);
	},
	invalidateQuote(authorizationToken: string, quoteLocator: string) {
		return patch(
			`${getApiUrl()}/quotes/${quoteLocator}/invalidate`,
			authorizationToken,
		);
	},
	getAutomatedUnderwritingResult(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	) {
		return get(
			`${getApiUrl()}/policy/${policyLocator}/automatedUnderwritingResult`,
			authorizationToken,
			config,
		);
	},
	getGraceLapseAndReinstatementInfo(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	) {
		return get(
			`${getApiUrl()}/policy/${policyLocator}/graceLapseReinstatements`,
			authorizationToken,
			config,
		);
	},
	getCancellationValue(
		authorizationToken: string,
		policyLocator: string,
		coverageEndTimestamp: string,
	) {
		return get(
			`${getApiUrl()}/policy/${policyLocator}/cancellationValue?coverageEndTimestamp=${coverageEndTimestamp}`,
			authorizationToken,
		);
	},
	withdrawPolicy(authorizationToken: string, policyLocator: string) {
		return post(
			`${getApiUrl()}/policy/${policyLocator}/withdraw`,
			authorizationToken,
		);
	},
	// updatePolicy is for policies without quotes
	updatePolicy(
		authorizationToken: string,
		policyLocator: string,
		params: PolicyUpdateRequest,
	) {
		return post(
			`${getApiUrl()}/policy/${policyLocator}/update`,
			authorizationToken,
			params,
		);
	},
	finalizePolicy(
		authorizationToken: string,
		policyLocator: string,
		params: PolicyUpdateRequest,
	) {
		return post(
			`${getApiUrl()}/policy/${policyLocator}/finalize`,
			authorizationToken,
			params,
		);
	},
	changePaymentPlan(
		authorizationToken: string,
		policyLocator: string,
		params: PaymentScheduleChangeRequest,
	) {
		return post(
			`${getApiUrl()}/policy/${policyLocator}/paymentSchedule`,
			authorizationToken,
			params,
		);
	},
	addFee(
		authorizationToken: string,
		policyLocator: string,
		params: FeeUpdateRequest,
	) {
		return patch(
			`${getApiUrl()}/policy/${policyLocator}/fees`,
			authorizationToken,
			params,
		);
	},
} as PolicyAPI;

export default PolicyApi;

export const getUsePolicyQueryKey = (
	policyLocator: string,
	authToken: string,
) => ['policy', policyLocator, authToken];

export const usePolicy = (policyLocator: string, authToken: string) => {
	const queryKey = getUsePolicyQueryKey(policyLocator, authToken);
	return useQuery({
		queryFn: async () => {
			const response = await PolicyApi.getPolicy(authToken, policyLocator);
			return response.data;
		},
		queryKey,
		enabled: !!policyLocator,
	});
};
